import { useEffect, useMemo, useState, Fragment, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, ChevronDown, ShieldCheck, ArrowRight, Loader2, Plus, Check, X, Columns3, Share2, ArrowDownWideNarrow } from "lucide-react";
import { api } from "@/lib/api";
import { useUI } from "@/context/UIContext";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/BusinessSection";
import {
  STATE_HERO, STATE_HERO_DEFAULT, STATE_SPECIES, STATE_ABBR, STATE_REGION, DRAW_BADGE,
} from "@/lib/stateMeta";

const heroImg = (s) => STATE_HERO[s] || STATE_HERO_DEFAULT;
const toSlug = (s) => encodeURIComponent(s);
const ABBR_TO_STATE = Object.fromEntries(Object.entries(STATE_ABBR).map(([name, ab]) => [ab, name]));
const FEATURED = ["Montana", "Colorado", "Idaho", "Oregon"];
const SPECIES_OPTS = ["Elk", "Mule Deer", "Whitetail Deer", "Pronghorn", "Black Bear", "Moose", "Wild Sheep", "Mountain Goat", "Wild Turkey", "Bison"];
const SYSTEM_OPTS = [["", "Any draw system"], ["otc_general", "OTC & Limited"], ["bonus_point", "Bonus Points"], ["preference_point", "Preference Points"], ["random_raffle", "Random Draw"]];
const REGION_OPTS = ["West", "Pacific", "Southwest", "Great Plains", "Midwest", "Southeast", "Northeast"];

const Select = ({ value, onChange, children, testid }) => (
  <div className="relative">
    <select value={value} onChange={(e) => onChange(e.target.value)} className="appearance-none bg-white border border-saddle/25 text-charcoal text-sm font-medium pl-3.5 pr-9 py-2.5 outline-none cursor-pointer hover:border-forest transition-colors rounded" data-testid={testid}>
      {children}
    </select>
    <ChevronDown size={15} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-charcoal/50 pointer-events-none" />
  </div>
);

export default function StatesDirectory() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { compareStates: compare, toggleCompare, clearCompare, persistCompare } = useUI();
  const [states, setStates] = useState(null);
  const [q, setQ] = useState("");
  const [scope, setScope] = useState("all"); // all | verified | coverage
  const [system, setSystem] = useState("");
  const [region, setRegion] = useState("");
  const [species, setSpecies] = useState("");
  const [sort, setSort] = useState("az"); // az | hunts
  const [showAll, setShowAll] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const didInit = useRef(false);

  useEffect(() => { api.exploreStates().then(setStates).catch(() => setStates([])); }, []);

  // Read ?compare=CO,ID from the URL once on mount into the shared compare store.
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    const raw = searchParams.get("compare");
    if (raw) {
      const names = raw.split(",").map((a) => ABBR_TO_STATE[a.trim().toUpperCase()]).filter(Boolean).slice(0, 3);
      if (names.length) persistCompare(names);
    }
  }, [searchParams, persistCompare]);

  // Keep the URL in sync with the shared compare store.
  useEffect(() => {
    if (!didInit.current) return;
    const abbrs = compare.map((n) => STATE_ABBR[n]).filter(Boolean).join(",");
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (abbrs) next.set("compare", abbrs); else next.delete("compare");
      return next;
    }, { replace: true });
  }, [compare, setSearchParams]);

  const togglePin = (name) => {
    if (!compare.includes(name) && compare.length >= 3) { toast.error("You can compare up to 3 states."); return; }
    toggleCompare(name);
  };

  const shareCompare = async () => {
    const abbrs = compare.map((n) => STATE_ABBR[n]).join(",");
    const url = `${window.location.origin}/explore?compare=${abbrs}`;
    try { if (navigator.share) { await navigator.share({ title: "HomeRange — Compare states", url }); return; } } catch { return; }
    try { await navigator.clipboard.writeText(url); toast.success("Compare link copied — send it to your hunting buddy."); } catch { toast.error("Couldn't copy the link."); }
  };

  const byName = useMemo(() => Object.fromEntries((states || []).map((s) => [s.state, s])), [states]);
  const featured = FEATURED.map((n) => byName[n]).filter(Boolean);

  const filtered = useMemo(() => {
    let list = states || [];
    const ql = q.trim().toLowerCase();
    if (ql) list = list.filter((s) => s.state.toLowerCase().includes(ql) || (STATE_ABBR[s.state] || "").toLowerCase() === ql);
    if (scope === "verified") list = list.filter((s) => s.has_data);
    if (scope === "coverage") list = list.filter((s) => !s.has_data);
    if (system) list = list.filter((s) => s.system === system);
    if (region) list = list.filter((s) => STATE_REGION[s.state] === region);
    if (species) list = list.filter((s) => (STATE_SPECIES[s.state] || []).includes(species));
    const sorted = [...list];
    if (sort === "hunts") sorted.sort((a, b) => (b.hunt_count || 0) - (a.hunt_count || 0) || a.state.localeCompare(b.state));
    else sorted.sort((a, b) => a.state.localeCompare(b.state));
    return sorted;
  }, [states, q, scope, system, region, species, sort]);

  const anyFilter = q || scope !== "all" || system || region || species;
  const visible = showAll || anyFilter ? filtered : filtered.slice(0, 15);

  return (
    <div className="bg-paper min-h-screen" data-testid="states-directory">
      <SiteHeader />

      {/* Hero */}
      <section className="relative bg-charcoal text-bone overflow-hidden" data-testid="states-hero">
        <div className="absolute inset-0 z-0">
          <img src={STATE_HERO_DEFAULT} alt="American wilderness at golden hour" className="w-full h-full object-cover object-center" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal via-charcoal/80 to-transparent" />
        </div>
        <div className="relative z-10 max-w-[1240px] mx-auto px-5 sm:px-10 py-16 lg:py-20">
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.92] tracking-tight">Hunt the country.</h1>
          <p className="text-bone/80 mt-4 max-w-md leading-relaxed">Explore hunting opportunities, draw systems, species, seasons, and official resources across all 50 states.</p>
        </div>
      </section>

      {/* Search + filters */}
      <div className="max-w-[1240px] mx-auto px-5 sm:px-10 py-5 flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="relative flex-1 lg:max-w-xs">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-charcoal/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search states…" className="w-full bg-white border border-saddle/25 rounded pl-10 pr-3 py-2.5 text-sm outline-none focus:border-forest" data-testid="states-search" />
        </div>
        <div className="flex flex-wrap items-center gap-2 lg:ml-auto">
          <Select value={scope} onChange={setScope} testid="filter-scope">
            <option value="all">All States</option>
            <option value="verified">Verified only</option>
            <option value="coverage">Coverage in progress</option>
          </Select>
          <button onClick={() => setScope((s) => (s === "verified" ? "all" : "verified"))} className={`inline-flex items-center gap-1.5 text-sm font-medium px-3.5 py-2.5 rounded border transition-colors ${scope === "verified" ? "bg-blaze text-white border-blaze" : "bg-white text-charcoal border-saddle/25 hover:border-forest"}`} data-testid="filter-verified">
            <ShieldCheck size={15} /> Verified Data
          </button>
          <Select value={system} onChange={setSystem} testid="filter-system">
            {SYSTEM_OPTS.map(([v, l]) => <option key={v} value={v}>{l === "Any draw system" ? l : l}</option>)}
          </Select>
          <Select value={region} onChange={setRegion} testid="filter-region">
            <option value="">Region</option>
            {REGION_OPTS.map((r) => <option key={r} value={r}>{r}</option>)}
          </Select>
          <Select value={species} onChange={setSpecies} testid="filter-species">
            <option value="">Species</option>
            {SPECIES_OPTS.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
          <div className="relative">
            <ArrowDownWideNarrow size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/45 pointer-events-none" />
            <select value={sort} onChange={(e) => setSort(e.target.value)} className="appearance-none bg-white border border-saddle/25 text-charcoal text-sm font-medium pl-8 pr-9 py-2.5 outline-none cursor-pointer hover:border-forest transition-colors rounded" data-testid="filter-sort">
              <option value="az">Sort: A–Z</option>
              <option value="hunts">Sort: Most verified hunts</option>
            </select>
            <ChevronDown size={15} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-charcoal/50 pointer-events-none" />
          </div>
        </div>
      </div>

      {!states ? (
        <div className="py-24 grid place-items-center"><Loader2 className="animate-spin text-forest" /></div>
      ) : (
        <main className="max-w-[1240px] mx-auto px-5 sm:px-10 pb-14">
          {/* Popular */}
          {!anyFilter && featured.length > 0 && (
            <section data-testid="popular-states">
              <div className="flex items-end justify-between gap-4">
                <h2 className="font-serif text-3xl sm:text-4xl text-charcoal tracking-tight">Popular Hunting States</h2>
                <button onClick={() => setShowAll(true)} className="text-sm font-semibold text-blaze hover:underline inline-flex items-center gap-1" data-testid="popular-viewall">View all states <ArrowRight size={14} /></button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-5">
                {featured.map((s) => (
                  <button key={s.state} onClick={() => navigate(`/states/${toSlug(s.state)}`)} className="group relative h-64 rounded-md overflow-hidden text-left" data-testid={`featured-${s.state}`}>
                    <img src={heroImg(s.state)} alt={`${s.state} landscape`} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/40 to-transparent" />
                    {s.has_data && <span className="absolute top-3 right-3 w-7 h-7 rounded-full bg-blaze grid place-items-center text-white shadow" data-testid={`featured-verified-${s.state}`}><ShieldCheck size={15} /></span>}
                    <div className="absolute inset-x-0 bottom-0 p-4">
                      <h3 className="font-serif text-2xl text-white tracking-wide uppercase drop-shadow">{s.state}</h3>
                      <p className="text-xs text-bone/85 mt-1 leading-snug">{(STATE_SPECIES[s.state] || []).slice(0, 4).join(" • ")}</p>
                      <span className="inline-flex items-center gap-1 text-sm font-semibold text-blaze mt-2 group-hover:gap-2 transition-all">Explore {s.state} <ArrowRight size={14} /></span>
                    </div>
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* All 50 */}
          <section className="mt-12" data-testid="all-states">
            <h2 className="font-serif text-3xl sm:text-4xl text-charcoal tracking-tight">Explore All 50 States</h2>
            <p className="text-charcoal/60 mt-1.5">Official resources, hunt information, draw systems and opportunities across the country.</p>

            {/* Region chips */}
            <div className="flex flex-wrap gap-2 mt-5" data-testid="region-chips">
              <button onClick={() => setRegion("")} className={`text-sm font-medium px-3.5 py-1.5 rounded-full border transition-colors ${region === "" ? "bg-charcoal text-bone border-charcoal" : "bg-white text-charcoal/70 border-saddle/25 hover:border-forest"}`} data-testid="region-chip-all">All Regions</button>
              {REGION_OPTS.map((r) => (
                <button key={r} onClick={() => setRegion((cur) => (cur === r ? "" : r))} className={`text-sm font-medium px-3.5 py-1.5 rounded-full border transition-colors ${region === r ? "bg-blaze text-white border-blaze" : "bg-white text-charcoal/70 border-saddle/25 hover:border-forest"}`} data-testid={`region-chip-${r}`}>{r}</button>
              ))}
            </div>

            {visible.length === 0 ? (
              <p className="text-charcoal/55 mt-8" data-testid="states-no-results">No states match your filters.</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mt-6" data-testid="states-grid">
                {visible.map((s) => {
                  const badge = DRAW_BADGE[s.system] || DRAW_BADGE.otc_general;
                  const pinned = compare.includes(s.state);
                  return (
                    <div key={s.state} onClick={() => navigate(`/states/${toSlug(s.state)}`)} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && navigate(`/states/${toSlug(s.state)}`)} className="group bg-white border border-saddle/20 rounded-md overflow-hidden text-left hover:-translate-y-1 hover:border-saddle/40 hover:shadow-md transition-all duration-200 cursor-pointer" data-testid={`state-card-${s.state}`}>
                      <div className="relative h-28">
                        <img src={heroImg(s.state)} alt={`${s.state} landscape`} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/50 to-transparent" />
                        <span className="absolute top-2 left-2.5 font-serif text-xl text-white tracking-wider drop-shadow">{STATE_ABBR[s.state]}</span>
                        <div className="absolute top-2 right-2 flex items-center gap-1.5">
                          {s.has_data && <span className="w-6 h-6 rounded-full bg-blaze grid place-items-center text-white" data-testid={`state-verified-${s.state}`}><ShieldCheck size={13} /></span>}
                          <button onClick={(e) => { e.stopPropagation(); togglePin(s.state); }} title={pinned ? "Remove from compare" : "Pin to compare"} className={`w-6 h-6 rounded-full grid place-items-center transition-colors ${pinned ? "bg-forest text-bone" : "bg-white/85 text-charcoal hover:bg-white"}`} data-testid={`pin-${s.state}`}>
                            {pinned ? <Check size={13} /> : <Plus size={13} />}
                          </button>
                        </div>
                      </div>
                      <div className="p-3">
                        <p className="font-serif text-lg text-charcoal leading-tight">{s.state}</p>
                        <span className={`inline-block text-[9px] font-bold uppercase tracking-wider px-2 py-1 rounded mt-1.5 ${badge.cls}`}>{badge.label}</span>
                        <p className={`text-xs mt-2 inline-flex items-center gap-1 ${s.has_data ? "text-forest" : "text-charcoal/50"}`}>
                          {s.has_data ? <><ShieldCheck size={12} className="text-blaze" /> {s.hunt_count} verified hunt{s.hunt_count === 1 ? "" : "s"}</> : "Coverage in progress"}
                        </p>
                        <span className="flex items-center gap-1 text-sm font-semibold text-saddle group-hover:text-blaze mt-2 transition-colors">Explore {s.state} <ArrowRight size={13} /></span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {!showAll && !anyFilter && filtered.length > 15 && (
              <div className="text-center mt-8">
                <button onClick={() => setShowAll(true)} className="inline-flex items-center gap-2 border border-blaze text-blaze font-semibold px-6 py-3 rounded hover:bg-blaze hover:text-white transition-colors" data-testid="view-all-50">View all 50 states <ArrowRight size={15} /></button>
              </div>
            )}
          </section>
        </main>
      )}

      {/* CTA */}
      <div className="max-w-[1240px] mx-auto px-5 sm:px-10 pb-14">
        <div className="relative bg-charcoal text-bone rounded-md overflow-hidden">
          <img src={STATE_HERO.Wyoming} alt="" className="absolute right-0 inset-y-0 w-1/2 h-full object-cover opacity-25" aria-hidden="true" />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal via-charcoal/90 to-charcoal/30" />
          <div className="relative px-6 sm:px-10 py-8 flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <div className="flex-1">
              <h3 className="font-serif text-3xl leading-tight">Not sure where to hunt next?</h3>
              <p className="text-bone/70 mt-2 max-w-lg">{`Tell HomeRange what you want to hunt, how many points you have, and how far you're willing to travel.`}</p>
            </div>
            <button onClick={() => navigate("/planner")} className="inline-flex items-center gap-2 bg-blaze text-white px-6 py-3.5 rounded font-semibold hover:bg-blaze-light transition-colors shrink-0" data-testid="cta-build-hunt">Build My Hunt <ArrowRight size={16} /></button>
          </div>
        </div>
      </div>

      {/* Compare bar */}
      {compare.length > 0 && (
        <div className="fixed bottom-0 inset-x-0 z-40 bg-charcoal text-bone border-t border-white/10" data-testid="compare-bar">
          <div className="max-w-[1240px] mx-auto px-5 sm:px-10 py-3 flex items-center gap-3 flex-wrap">
            <span className="text-sm text-bone/70 shrink-0"><Columns3 size={16} className="inline mr-1.5 -mt-0.5" />Compare</span>
            <div className="flex items-center gap-2 flex-wrap">
              {compare.map((n) => (
                <span key={n} className="inline-flex items-center gap-1.5 bg-white/10 rounded-full pl-3 pr-1.5 py-1 text-sm" data-testid={`compare-chip-${n}`}>
                  {n}
                  <button onClick={() => togglePin(n)} className="w-4 h-4 rounded-full bg-white/15 hover:bg-blaze grid place-items-center" aria-label={`Remove ${n}`} data-testid={`compare-remove-${n}`}><X size={11} /></button>
                </span>
              ))}
            </div>
            <div className="ml-auto flex items-center gap-3 shrink-0">
              <button onClick={shareCompare} className="inline-flex items-center gap-1.5 text-sm text-bone/80 hover:text-bone" data-testid="compare-share"><Share2 size={15} /> Share</button>
              <button onClick={clearCompare} className="text-sm text-bone/60 hover:text-bone" data-testid="compare-clear">Clear</button>
              <button onClick={() => setCompareOpen(true)} disabled={compare.length < 2} className="bg-blaze text-white px-4 py-2 rounded text-sm font-semibold hover:bg-blaze-light transition-colors disabled:opacity-50" data-testid="compare-open">Compare {compare.length} state{compare.length === 1 ? "" : "s"}</button>
            </div>
          </div>
        </div>
      )}

      {compareOpen && (
        <CompareModal states={compare.map((n) => byName[n]).filter(Boolean)} onClose={() => setCompareOpen(false)} onGo={(s) => navigate(`/states/${toSlug(s)}`)} />
      )}

      {compare.length > 0 && <div aria-hidden className="h-16" />}
      <Footer />
    </div>
  );
}

function CompareModal({ states, onClose, onGo }) {
  const ROWS = [
    { label: "Region", get: (s) => STATE_REGION[s.state] || "—" },
    { label: "Draw system", get: (s) => (DRAW_BADGE[s.system] || DRAW_BADGE.otc_general).label },
    { label: "Verified hunts", get: (s) => (s.has_data ? `${s.hunt_count}` : "Coverage in progress") },
    { label: "Huntable species", get: (s) => (STATE_SPECIES[s.state] || []).join(", ") || "—" },
  ];
  return (
    <div className="fixed inset-0 z-[80] bg-charcoal/60 grid place-items-center p-4" onClick={onClose} data-testid="compare-modal">
      <div className="bg-paper w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-saddle/20 sticky top-0 bg-paper">
          <h3 className="font-serif text-2xl text-charcoal">Compare states</h3>
          <button onClick={onClose} aria-label="Close" data-testid="compare-close"><X size={20} className="text-charcoal/50" /></button>
        </div>
        <div className="p-5">
          <div className="grid gap-3" style={{ gridTemplateColumns: `140px repeat(${states.length}, 1fr)` }}>
            {/* header row */}
            <div />
            {states.map((s) => (
              <div key={s.state} className="text-center">
                <div className="relative h-20 rounded overflow-hidden">
                  <img src={STATE_HERO[s.state] || STATE_HERO_DEFAULT} alt={s.state} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-charcoal/30" />
                  <span className="absolute inset-0 grid place-items-center font-serif text-lg text-white drop-shadow">{s.state}</span>
                </div>
              </div>
            ))}
            {/* rows */}
            {ROWS.map((row) => (
              <Fragment key={row.label}>
                <div className="text-xs uppercase tracking-wide text-charcoal/50 py-3 border-t border-saddle/12 flex items-center">{row.label}</div>
                {states.map((s) => (
                  <div key={`${row.label}-${s.state}`} className="text-sm text-charcoal/85 py-3 border-t border-saddle/12 text-center px-1">{row.get(s)}</div>
                ))}
              </Fragment>
            ))}
            {/* actions */}
            <div />
            {states.map((s) => (
              <div key={`go-${s.state}`} className="text-center pt-3">
                <button onClick={() => onGo(s.state)} className="inline-flex items-center gap-1 text-sm font-semibold text-blaze hover:underline" data-testid={`compare-go-${s.state}`}>Explore <ArrowRight size={13} /></button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
